import { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AirplaneTilt, Package, Scales, Calendar, CurrencyDollar, MapPin, CheckCircle, ArrowRight, ArrowLeft, Info, Sparkle, Plus, X } from '@phosphor-icons/react';
import Navigation from '@/components/Navigation';
import LocationPicker from '@/components/LocationPicker';
import { API } from '@/App';
import { toast } from 'sonner';

const STEPS = [
  { num: 1, title: 'Trip Details', icon: AirplaneTilt },
  { num: 2, title: 'Capacity', icon: Scales },
  { num: 3, title: 'Preferences', icon: Package },
  { num: 4, title: 'Pricing', icon: CurrencyDollar }
];

const CreateJourney = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    departure_country: '', departure_city: '', destination_country: '', destination_city: '',
    departure_date: '', arrival_date: '',
    flight_number: '', airline: '',
    capacity_mode: 'single',
    available_weight: 15,
    slots: [{ weight: 5 }, { weight: 5 }, { weight: 5 }],
    delivery_method: 'in-person',
    pickup_preference: 'sender-comes',
    allowed_categories: ['Documents', 'Clothing'],
    price_per_kg: 12
  });

  const [aiRecommendation, setAiRecommendation] = useState({
    min: 8, max: 16, recommended: 12,
    reason: 'Default market estimate. Enter your route and click "Get AI recommendation" for a tailored price.',
    loading: false,
  });

  const fetchAiRecommendation = async () => {
    if (!form.departure_country || !form.destination_country) {
      toast.error('Set departure and destination first.');
      return;
    }
    setAiRecommendation((p) => ({ ...p, loading: true }));
    try {
      const res = await fetch(`${API}/pricing/recommend`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          origin_country: form.departure_country,
          destination_country: form.destination_country,
          weight: form.available_weight,
          category: (form.allowed_categories || [])[0] || 'Documents',
          urgency: 'standard',
        }),
      });
      const data = await res.json();
      setAiRecommendation({
        min: data.low_per_kg, max: data.high_per_kg, recommended: data.recommended_per_kg,
        reason: data.reasoning, loading: false,
      });
    } catch (e) {
      setAiRecommendation((p) => ({ ...p, loading: false }));
      toast.error('AI recommendation unavailable.');
    }
  };

  const aiFetchedRef = useRef(false);
  // Auto-fetch when entering step 4 with a complete route.
  useEffect(() => {
    if (step === 4 && form.departure_country && form.destination_country && !aiFetchedRef.current) {
      aiFetchedRef.current = true;
      fetchAiRecommendation();
    }
  }, [step]);

  const update = (patch) => setForm({ ...form, ...patch });

  const next = () => setStep(Math.min(step + 1, 4));
  const back = () => setStep(Math.max(step - 1, 1));

  const submit = async () => {
    setLoading(true);
    try {
      const totalWeight = form.capacity_mode === 'single'
        ? form.available_weight
        : form.slots.reduce((s, sl) => s + (parseFloat(sl.weight) || 0), 0);

      const res = await fetch(`${API}/trips`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          departure_country: form.departure_country,
          departure_city: form.departure_city,
          destination_country: form.destination_country,
          destination_city: form.destination_city,
          departure_date: form.departure_date,
          arrival_date: form.arrival_date,
          available_weight: totalWeight,
          price_per_kg: parseFloat(form.price_per_kg),
          capacity_mode: form.capacity_mode,
          allowed_categories: form.allowed_categories,
          compliance_declaration_accepted: true,
        })
      });
      if (!res.ok) throw new Error('Failed to create journey');
      toast.success('Journey published successfully!');
      navigate('/dashboard');
    } catch (e) {
      toast.error(e.message);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }} data-testid="journey-title">
            Create your journey
          </h1>
          <p className="text-gray-600 mt-1">Earn money by carrying parcels on your upcoming trip</p>
        </div>

        {/* Step Progress */}
        <div className="bg-white rounded-2xl border border-gray-100 p-5 mb-6">
          <div className="flex items-center justify-between">
            {STEPS.map((s, idx) => {
              const Icon = s.icon;
              const isActive = step === s.num;
              const isDone = step > s.num;
              return (
                <div key={s.num} className="flex items-center flex-1">
                  <div className="flex items-center gap-3" data-testid={`step-indicator-${s.num}`}>
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition-all ${
                      isActive ? 'dropio-gradient text-white' : isDone ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'
                    }`}>
                      {isDone ? <CheckCircle size={20} weight="fill" /> : <Icon size={18} weight="bold" />}
                    </div>
                    <div className="hidden md:block">
                      <p className={`text-xs font-bold uppercase tracking-wider ${isActive ? 'text-[#6D28D9]' : isDone ? 'text-green-600' : 'text-gray-400'}`}>
                        Step {s.num}
                      </p>
                      <p className={`text-sm font-semibold ${isActive || isDone ? 'text-gray-900' : 'text-gray-400'}`}>{s.title}</p>
                    </div>
                  </div>
                  {idx < STEPS.length - 1 && (
                    <div className={`flex-1 h-0.5 mx-3 transition-colors ${step > s.num ? 'bg-green-300' : 'bg-gray-200'}`}></div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Step Content */}
        <div className="bg-white rounded-2xl border border-gray-100 p-8">
          {step === 1 && <TripDetailsStep form={form} update={update} />}
          {step === 2 && <CapacityStep form={form} update={update} />}
          {step === 3 && <PreferencesStep form={form} update={update} />}
          {step === 4 && <PricingStep form={form} update={update} ai={aiRecommendation} onRefetch={fetchAiRecommendation} />}

          <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-100">
            <button
              onClick={back} disabled={step === 1}
              className="px-5 py-2.5 border border-gray-200 rounded-lg text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              data-testid="step-back"
            >
              <ArrowLeft size={16} weight="bold" /> Back
            </button>
            {step < 4 ? (
              <button
                onClick={next}
                className="dropio-gradient-cta text-white font-semibold px-6 py-2.5 rounded-lg shadow-md flex items-center gap-2"
                data-testid="step-next"
              >
                Continue <ArrowRight size={16} weight="bold" />
              </button>
            ) : (
              <button
                onClick={submit} disabled={loading}
                className="dropio-gradient-cta text-white font-semibold px-6 py-2.5 rounded-lg shadow-md disabled:opacity-60 flex items-center gap-2"
                data-testid="step-publish"
              >
                {loading ? 'Publishing...' : 'Publish Journey'} <CheckCircle size={16} weight="bold" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const TripDetailsStep = ({ form, update }) => (
  <div className="space-y-6">
    <div>
      <h2 className="text-xl font-bold text-gray-900 mb-1">Where are you flying?</h2>
      <p className="text-sm text-gray-500">Tell us about your upcoming trip — Beta routes: Australia ↔ Bangladesh</p>
    </div>
    <div className="space-y-5">
      <div>
        <label className="block text-xs font-semibold text-gray-600 mb-2">Departure (Country · City)</label>
        <LocationPicker
          value={{ country: form.departure_country, city: form.departure_city }}
          onChange={({ country, city }) => update({ departure_country: country, departure_city: city })}
          testIdPrefix="dep"
        />
      </div>
      <div>
        <label className="block text-xs font-semibold text-gray-600 mb-2">Destination (Country · City)</label>
        <LocationPicker
          value={{ country: form.destination_country, city: form.destination_city }}
          onChange={({ country, city }) => update({ destination_country: country, destination_city: city })}
          testIdPrefix="dest"
        />
      </div>
    </div>
    <div className="grid md:grid-cols-2 gap-5">
      <FieldInput label="Departure date" icon={Calendar} type="date" value={form.departure_date} onChange={(v) => update({ departure_date: v })} testId="dep-date" />
      <FieldInput label="Arrival date" icon={Calendar} type="date" value={form.arrival_date} onChange={(v) => update({ arrival_date: v })} testId="arr-date" />
      <FieldInput label="Airline (optional)" icon={AirplaneTilt} value={form.airline} onChange={(v) => update({ airline: v })} placeholder="e.g. Qantas" testId="airline" />
      <FieldInput label="Flight number (optional)" icon={AirplaneTilt} value={form.flight_number} onChange={(v) => update({ flight_number: v })} placeholder="e.g. QF123" testId="flight-num" />
    </div>
  </div>
);

const CapacityStep = ({ form, update }) => (
  <div className="space-y-6">
    <div>
      <h2 className="text-xl font-bold text-gray-900 mb-1">How much can you carry?</h2>
      <p className="text-sm text-gray-500">Choose your capacity setup</p>
    </div>

    <div className="grid md:grid-cols-2 gap-4">
      <button
        type="button" onClick={() => update({ capacity_mode: 'single' })}
        className={`p-5 rounded-xl border-2 text-left transition-all ${
          form.capacity_mode === 'single' ? 'border-[#6D28D9] bg-purple-50' : 'border-gray-200 hover:border-gray-300'
        }`}
        data-testid="capacity-single"
      >
        <Package size={28} weight="duotone" className={form.capacity_mode === 'single' ? 'text-[#6D28D9] mb-2' : 'text-gray-400 mb-2'} />
        <p className="font-bold text-gray-900 mb-1">Single Booking</p>
        <p className="text-xs text-gray-600">Reserve your entire capacity for one sender — simpler logistics, fewer pickups.</p>
      </button>
      <button
        type="button" onClick={() => update({ capacity_mode: 'multiple' })}
        className={`p-5 rounded-xl border-2 text-left transition-all ${
          form.capacity_mode === 'multiple' ? 'border-[#6D28D9] bg-purple-50' : 'border-gray-200 hover:border-gray-300'
        }`}
        data-testid="capacity-multiple"
      >
        <Scales size={28} weight="duotone" className={form.capacity_mode === 'multiple' ? 'text-[#6D28D9] mb-2' : 'text-gray-400 mb-2'} />
        <p className="font-bold text-gray-900 mb-1">Multiple Bookings</p>
        <p className="text-xs text-gray-600">Split capacity across multiple senders to maximize earnings.</p>
      </button>
    </div>

    {form.capacity_mode === 'single' ? (
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-semibold text-gray-700">Total available capacity</label>
          <span className="text-lg font-bold text-[#6D28D9]">{form.available_weight} kg</span>
        </div>
        <input type="range" min="1" max="40" value={form.available_weight} onChange={(e) => update({ available_weight: parseInt(e.target.value) })} className="w-full accent-[#6D28D9]" data-testid="capacity-weight" />
      </div>
    ) : (
      <div>
        <label className="text-sm font-semibold text-gray-700 block mb-3">Configure your slots</label>
        <div className="space-y-2">
          {form.slots.map((slot, idx) => (
            <div key={idx} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg" data-testid={`slot-${idx}`}>
              <span className="text-sm font-semibold text-gray-700 w-16">Slot {idx + 1}</span>
              <input type="number" min="1" value={slot.weight} onChange={(e) => {
                const slots = [...form.slots];
                slots[idx] = { weight: parseFloat(e.target.value) || 0 };
                update({ slots });
              }} className="flex-1 px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm" />
              <span className="text-sm text-gray-500">kg</span>
              <button type="button" onClick={() => update({ slots: form.slots.filter((_, i) => i !== idx) })} className="text-gray-400 hover:text-red-500">
                <X size={18} weight="bold" />
              </button>
            </div>
          ))}
          <button type="button" onClick={() => update({ slots: [...form.slots, { weight: 5 }] })} className="w-full py-2.5 border border-dashed border-gray-300 text-gray-600 rounded-lg text-sm font-semibold hover:border-[#6D28D9] hover:text-[#6D28D9] flex items-center justify-center gap-1" data-testid="add-slot">
            <Plus size={16} weight="bold" /> Add slot
          </button>
        </div>
        <p className="text-sm text-gray-600 mt-3">Total: <strong className="text-gray-900">{form.slots.reduce((s, sl) => s + (parseFloat(sl.weight) || 0), 0)} kg</strong></p>
      </div>
    )}
  </div>
);

const PreferencesStep = ({ form, update }) => {
  const categories = ['Documents', 'Clothing', 'Baby Products', 'Books', 'Electronics', 'Medicine', 'Food (sealed)', 'Toys', 'Cosmetics'];
  const toggleCat = (c) => {
    const has = form.allowed_categories.includes(c);
    update({ allowed_categories: has ? form.allowed_categories.filter((x) => x !== c) : [...form.allowed_categories, c] });
  };
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-1">Set your preferences</h2>
        <p className="text-sm text-gray-500">Define what and how you&apos;ll deliver</p>
      </div>

      <div>
        <label className="text-sm font-semibold text-gray-700 block mb-3">Delivery method</label>
        <div className="grid md:grid-cols-2 gap-3">
          {[
            { id: 'in-person', label: 'In-person handover', desc: 'Meet sender/recipient face-to-face' },
            { id: 'drop-off', label: 'Drop-off point', desc: 'Use designated drop-off location' }
          ].map((opt) => (
            <button key={opt.id} type="button" onClick={() => update({ delivery_method: opt.id })}
              className={`p-4 rounded-xl border-2 text-left ${form.delivery_method === opt.id ? 'border-[#6D28D9] bg-purple-50' : 'border-gray-200'}`}
              data-testid={`delivery-${opt.id}`}>
              <p className="font-semibold text-gray-900 text-sm">{opt.label}</p>
              <p className="text-xs text-gray-600 mt-0.5">{opt.desc}</p>
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="text-sm font-semibold text-gray-700 block mb-3">Pickup preference</label>
        <div className="grid md:grid-cols-2 gap-3">
          {[
            { id: 'sender-comes', label: 'Sender comes to me', desc: 'No travel for pickup' },
            { id: 'i-pickup', label: "I'll pick up", desc: 'Flexible — additional fee may apply' }
          ].map((opt) => (
            <button key={opt.id} type="button" onClick={() => update({ pickup_preference: opt.id })}
              className={`p-4 rounded-xl border-2 text-left ${form.pickup_preference === opt.id ? 'border-[#6D28D9] bg-purple-50' : 'border-gray-200'}`}
              data-testid={`pickup-${opt.id}`}>
              <p className="font-semibold text-gray-900 text-sm">{opt.label}</p>
              <p className="text-xs text-gray-600 mt-0.5">{opt.desc}</p>
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="text-sm font-semibold text-gray-700 block mb-3">Allowed parcel categories</label>
        <div className="flex flex-wrap gap-2">
          {categories.map((c) => {
            const on = form.allowed_categories.includes(c);
            return (
              <button key={c} type="button" onClick={() => toggleCat(c)}
                className={`px-3 py-1.5 rounded-full text-sm font-semibold transition-all ${
                  on ? 'dropio-gradient-cta text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                data-testid={`cat-${c.toLowerCase().replace(/\s+/g, '-')}`}>
                {on && '✓ '}{c}
              </button>
            );
          })}
        </div>
        <div className="mt-3 flex items-start gap-2 p-3 bg-blue-50 rounded-lg">
          <Info size={16} weight="fill" className="text-blue-600 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-blue-900">Restricted items (weapons, narcotics, etc.) are automatically blocked by our AI compliance engine — no manual filtering needed.</p>
        </div>
      </div>
    </div>
  );
};

const PricingStep = ({ form, update, ai, onRefetch }) => (
  <div className="space-y-6">
    <div>
      <h2 className="text-xl font-bold text-gray-900 mb-1">Set your price</h2>
      <p className="text-sm text-gray-500">You decide. Our AI helps you stay competitive.</p>
    </div>

    {/* AI Recommendation (Claude Sonnet 4.5) */}
    <div className="dropio-gradient rounded-2xl p-5 text-white relative overflow-hidden" data-testid="ai-recommendation">
      <Sparkle size={48} weight="duotone" className="absolute -top-2 -right-2 opacity-20" />
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center backdrop-blur-sm flex-shrink-0">
          <Sparkle size={20} weight="fill" />
        </div>
        <div className="flex-1">
          <div className="flex items-center justify-between">
            <p className="text-xs font-bold uppercase tracking-widest opacity-90 mb-1">AI Price Recommendation</p>
            <button type="button" onClick={onRefetch} disabled={ai.loading}
                    className="text-[10px] font-bold uppercase bg-white/20 hover:bg-white/30 px-2 py-1 rounded backdrop-blur-sm disabled:opacity-50"
                    data-testid="refetch-ai-price">
              {ai.loading ? 'Thinking…' : 'Refresh'}
            </button>
          </div>
          <p className="text-3xl font-extrabold mb-2" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>${ai.recommended}/kg</p>
          <p className="text-sm text-white/90 mb-2">Market range: ${ai.min}–${ai.max}/kg</p>
          <p className="text-xs text-white/80 leading-relaxed line-clamp-5" data-testid="ai-reason">{ai.reason}</p>
          <button onClick={() => update({ price_per_kg: ai.recommended })} className="mt-3 bg-white text-[#6D28D9] font-semibold px-4 py-1.5 rounded-lg text-sm hover:bg-purple-50" data-testid="use-recommendation">
            Use recommended price
          </button>
        </div>
      </div>
    </div>

    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="text-sm font-semibold text-gray-700">Your price per kg</label>
        <span className="text-2xl font-bold text-[#6D28D9]">${form.price_per_kg}/kg</span>
      </div>
      <input type="range" min="5" max="30" value={form.price_per_kg} onChange={(e) => update({ price_per_kg: parseInt(e.target.value) })} className="w-full accent-[#6D28D9]" data-testid="price-slider" />
      <div className="flex justify-between text-xs text-gray-500 mt-1">
        <span>$5</span><span>$30</span>
      </div>
    </div>

    {/* Earnings preview */}
    <div className="bg-gray-50 rounded-xl p-5 border border-gray-100">
      <p className="text-xs font-bold text-gray-600 uppercase tracking-wider mb-3">Estimated earnings</p>
      <div className="space-y-1.5 text-sm">
        <div className="flex justify-between">
          <span className="text-gray-600">Gross ({form.available_weight}kg × ${form.price_per_kg}/kg)</span>
          <span className="font-semibold text-gray-900">${form.available_weight * form.price_per_kg}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">Platform fee (8%)</span>
          <span className="font-semibold text-gray-500">–${(form.available_weight * form.price_per_kg * 0.08).toFixed(2)}</span>
        </div>
        <div className="flex justify-between pt-2 border-t border-gray-200 mt-2">
          <span className="font-bold text-gray-900">You take home</span>
          <span className="font-bold text-[#6D28D9] text-lg">${(form.available_weight * form.price_per_kg * 0.92).toFixed(2)}</span>
        </div>
      </div>
    </div>
  </div>
);

const FieldInput = ({ label, icon: Icon, type = 'text', value, onChange, placeholder, testId }) => (
  <div>
    <label className="text-sm font-semibold text-gray-700 block mb-1.5">{label}</label>
    <div className="relative">
      <Icon size={16} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
      <input
        type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#6D28D9] focus:ring-2 focus:ring-purple-100"
        data-testid={testId}
      />
    </div>
  </div>
);

export default CreateJourney;
